import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { Entry } from 'contentful';
import { combineLatest, Observable, Subject } from 'rxjs';
import { User } from '../../interfaces/user';
import { AuthService } from '../../services/auth/auth.service';
import { LocalizationService } from '../../services/localization/localization.service';
import { ProfileService } from '../../services/profile/profile.service';
import { GlobalContentService } from '../../services/global-content/global-content.service';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements OnInit {
  @Input() public content: Entry<any> | undefined;
  @Output() public signInCompleted = new EventEmitter<void>();
  public signInForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6)
    ])
  });
  public authError$: Observable<firebase.auth.Error | null>;
  public authSsoError$: Observable<firebase.auth.Error | null>;
  public formError$ = new Subject<string | null>();
  public showCompleteProfile = false;
  public globalContent$: Observable<Entry<any>>;

  constructor(
    public authService: AuthService,
    private profileService: ProfileService,
    private localizationService: LocalizationService,
    private globalContentService: GlobalContentService
  ) {
    this.authError$ = authService.authError();
    this.authSsoError$ = authService.authSsoError();
  }

  ngOnInit() {
    this.authService.resetAuthSsoError();

    this.authService.isLoggedIn().subscribe(loggedin => {
      if (loggedin) {
        this.profileService.userDocument().subscribe(user => {
          user.valueChanges().subscribe(user => {
            if (!user || !user.firstName) {
              console.log('user document not detected');
              this.showCompleteProfile = true;
            }
          });
        });
      }
    });

    combineLatest([
      this.authService.isLoggedIn(),
      this.profileService.userDocumentDefines([
        'firstName',
        'lastName',
        'newsletter'
      ])
    ]).subscribe(([loggedIn, isComplete]) => {
      if (!loggedIn) {
        this.showCompleteProfile = false;
      } else if (!isComplete) {
        this.showCompleteProfile = true;
      } else {
        this.signInCompleted.emit();
      }
    });

    this.globalContent$ = this.globalContentService.getGlobalContent();
  }

  get email() {
    return this.signInForm.get('email');
  }

  get password() {
    return this.signInForm.get('password');
  }

  hasError(formControl: AbstractControl, validator: string) {
    return (
      formControl.touched &&
      formControl.dirty &&
      formControl.hasError(validator)
    );
  }

  signInWithEmailAndPassword() {
    Object.values(this.signInForm.controls).forEach(control => {
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    });

    if (this.signInForm.valid) {
      this.formError$.next(null);
      const { email, password } = this.signInForm.value;
      this.authService.signInWithEmailAndPassword(email, password);
    } else {
      this.authService.resetAuthError();
      this.formError$.next('Invalid fields.');
    }
  }

  updateUserDocument(formData: Partial<User>) {
    this.profileService.updateUserDocument({
      ...formData,
      locale: this.localizationService.locale.value
    });
    this.signInCompleted.emit();
  }
}
